import { useState, useCallback, useEffect } from "react";
import leaveTypeService from "../services/leaveTypeService";
import { SetAuditTrail, SetAuditTrailBulk } from "../../components/Reports/AuditTrailReportEmployeeSetup";
import { useGeneralMessage } from "../../common/APICommon";
import { useEnumsApi } from "./useEnumsApi";

export const useLeaveType = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [activeData, setActiveData] = useState([]);
  const [filterData, setFilterData] = useState([]);

  const { showGeneralMessage, showMessage, message, isSuccess } = useGeneralMessage();

  // 1. Fetch the Leave Types list
  const fetchLeaveTypes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await leaveTypeService.getAll();
      // MODIFIED: No more rawData/mapping needed. Backend returns 'taxTreatmentName' now.
      const responseData = res.data || [];
      setData(responseData);
      setFilterData(responseData);
    } catch (err) {
      console.error("Failed to fetch leave types", err);
      showGeneralMessage("Failed to load leave types", false);
    } finally {
      setLoading(false);
    }
  }, [showGeneralMessage]);

  // Initial fetch on mount
  useEffect(() => {
    fetchLeaveTypes();
  }, [fetchLeaveTypes]);

  const fetchActiveLeaveTypes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await leaveTypeService.getActive();
      // MODIFIED: No more rawData/mapping needed. Backend returns 'taxTreatmentName' now.
      const responseData = res.data || [];
      setActiveData(responseData);
    } catch (err) {
      console.error("Failed to fetch leave types", err);
      showGeneralMessage("Failed to load leave types", false);
    } finally {
      setLoading(false);
    }
  }, [showGeneralMessage]);

  useEffect(() => {
    fetchActiveLeaveTypes();
  }, [fetchActiveLeaveTypes]);

  const handleAction = async (actionFn, auditData, ...args) => {
    setLoading(true);
    try {
      console.log("Args: ", args);
      const res = await actionFn(...args);

      if (Array.isArray(auditData)) {
        await SetAuditTrailBulk({ data: auditData });
      } else if (auditData) {
        await SetAuditTrail(auditData);
      }

      showGeneralMessage(res.data?.message || "Action successful", true);
      await fetchLeaveTypes();
      return { success: true, data: res.data };
    } catch (err) {
      const errorMsg = err.response?.data?.message || "An error occurred";
      showGeneralMessage(errorMsg, false);
      return { success: false };
    } finally {
      setLoading(false);
    }
  };

  const searchData = (value) => {
    if (!value) {
      setData(filterData);
      return;
    }
    const lower = value.toLowerCase();
    setData(filterData.filter(f =>
      f.description?.toLowerCase().includes(lower) ||
      f.id?.toString().includes(lower) ||
      f.taxTreatmentName?.toLowerCase().includes(lower) // ADDED: Can now search by tax treatment name
    ));
  };

  return {
    loading: loading,
    data,
    activeData,
    showMessage,
    message,
    isSuccess,
    searchData,
    createLeaveType: (p, audit) => handleAction(leaveTypeService.create, audit, p),
    updateLeaveType: (id, p, audit) => handleAction(leaveTypeService.update, audit, id, p),
    deleteLeaveType: (id) => handleAction(leaveTypeService.delete, null, id),
    refresh: fetchLeaveTypes
  };
};